import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Tag, Plus, X, Edit, Trash2, AlertCircle, Check
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function CategoryManagement() {
  const { getAuthHeader, user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      fetchCategories();
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/categories?active_only=false`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Category name is required');
      return;
    }

    try {
      await axios.post(`${API}/categories`, formData, {
        headers: getAuthHeader()
      });
      
      toast.success(`Category created: ${formData.name}`);
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/categories/${categoryId}`, {
        headers: getAuthHeader()
      });
      
      if (response.data.deactivated) {
        toast.info(`Category deactivated (in use by repairs)`);
      } else {
        toast.success(`Category deleted: ${categoryName}`);
      }
      
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleToggleActive = async (categoryId, currentStatus) => {
    try {
      await axios.put(
        `${API}/categories/${categoryId}`,
        { active: !currentStatus },
        { headers: getAuthHeader() }
      );
      
      toast.success(currentStatus ? 'Category deactivated' : 'Category activated');
      fetchCategories();
    } catch (error) {
      console.error('Error toggling category:', error);
      toast.error('Failed to update category');
    }
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800">Access Denied</h2>
        <p className="text-gray-600 mt-2">Admin or Super Admin access required</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#193460] flex items-center gap-2">
            <Tag className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" />
            <span className="truncate">Category Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Manage repair categories</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#E87B6A] hover:bg-[#d66a59] text-white w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Category
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
          <div className="text-sm text-gray-600">Total Categories</div>
          <div className="text-2xl font-bold text-gray-800">{categories.length}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
          <div className="text-sm text-green-600">Active</div>
          <div className="text-2xl font-bold text-green-800">
            {categories.filter(c => c.active).length}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
          <div className="text-sm text-gray-600">Inactive</div>
          <div className="text-2xl font-bold text-gray-800">
            {categories.filter(c => !c.active).length}
          </div>
        </div>
      </div>

      {/* Category List */}
      <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="text-left p-4 font-semibold text-gray-700">Category Name</th>
              <th className="text-left p-4 font-semibold text-gray-700">Description</th>
              <th className="text-left p-4 font-semibold text-gray-700">Status</th>
              <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center p-8 text-gray-500">
                  Loading categories...
                </td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center p-8 text-gray-500">
                  No categories found
                </td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr key={category.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#E87B6A]" />
                      <span className="font-semibold text-gray-800">{category.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-600">
                    {category.description || '-'}
                  </td>
                  <td className="p-4">
                    <Badge className={
                      category.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }>
                      {category.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        onClick={() => handleToggleActive(category.id, category.active)}
                        variant="outline"
                        size="sm"
                      >
                        {category.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      {user?.role === 'super_admin' && (
                        <Button
                          onClick={() => handleDeleteCategory(category.id, category.name)}
                          variant="destructive"
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Category Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-[#193460]">Create New Category</h2>
              <button onClick={() => setShowCreateModal(false)}>
                <X className="w-6 h-6 text-gray-600 hover:text-gray-800" />
              </button>
            </div>

            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <Label htmlFor="name">Category Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Computer Repairs"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description of this category..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-[#E87B6A] hover:bg-[#d66a59] text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Create Category
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
